import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import HttpService from '../services/HttpServices';
import { toast } from 'react-hot-toast';

const apiService = HttpService;

// Initial state of the slice
const initialState = {
  shipmentData: [], // List of shipments for the inbox and outbox
  buyers: [], // List of buyers for a company
  shipmentDetails: {}, // Details of a single shipment
  buyerBusinessUnit: [],
  sellerBusinessUnit: [],
  linkingOrders: [],
  productData: [],
  productDetails: [],
  productList: [],
  stockList: [], // List of all available stocks
  stockData: [], // contains all stocks of a particular product
  stockDetails: [], // single stock details
  stocks: [], // all stocks in a shipment
  uom: [],
  totalRecords: 0,
  stockTotalRecords: 0,
  loading: false,
  error: null,
};

export const validateShipmentLotExistence = async (shipmentId, type) => {
  try {
    const response = await apiService.get(`/shipments/${shipmentId}`, { params: { type } });
    // console.log("response=",response);
    return response.data.data
  } catch (err) {
    return err
  }
}

export const validateShipmentStockLot = async (shipmentId, type) => {
  try {
    const response = await apiService.get(`/shipments/${shipmentId}`, { params: { type } });
    // console.log("response validateShipmentStockLot",response);
    return response.data.data
  } catch (err) {
    return err
  }
}

export const fetchUOM = createAsyncThunk(
  'shipment/fetchUOM',
  async ({ type }, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/orders/uom`, { params: { type } });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const fetchProducts = createAsyncThunk(
  'shipment/fetchProducts',
  async (queryParams, thunkAPI) => {
    try {
      const endPoint = '/shipments/products';
      const response = await apiService.get(endPoint);
      const products = response.data.data
      return products;
    } catch (error) {
      // console.log("error : ", error)

      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching shipments for the inbox
export const fetchShipments = createAsyncThunk(
  'shipment/fetchShipments',
  async (queryParams, thunkAPI) => {
    try {
      // console.log('check:', `${apiService}`)
      const response = await apiService.get('/shipments',
        { params: queryParams },
      );
      // console.log("response : ", response)
      return response.data;
    } catch (error) {
      // console.log("error : ", error)

      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for creating an shipment
export const createShipment = createAsyncThunk(
  'shipment/createShipment',
  async (shipmentData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('/shipments', shipmentData);
      // console.log("response.data",response.data)
      // toast.success('Shipment Created successfully');
      return response?.data?.data?.shipmentId; // response
    } catch (error) {
      // console.log("error.message",error.message)
      const errorMessage = error.response?.data?.msg || "An unexpected error occurred";
      toast.error(errorMessage);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching an shipment by ID
export const fetchShipmentById = createAsyncThunk(
  'shipment/fetchShipmentById',
  async ({ shipmentId, type }, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/shipments/${shipmentId}`, { params: { type } });
      // console.log("response.data",response.data);
      return response.data; // response
    } catch (error) {
      // console.error("Failed to fetch shipment details:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const editShipment = createAsyncThunk(
  'shipment/editShipment',
  async ({ shipmentId, shipmentData }, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`/shipments/${shipmentId}`, shipmentData);
      return response.data; // Assuming your API returns the updated shipment data
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const fetchBuyersByCompanyId = createAsyncThunk(
  'shipment/fetchBuyersByCompanyId',
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/shipments/buyer-companies');
      return response.data; // Adjust if your API response structure is different
    } catch (error) {
      // console.error("Failed to fetch buyers:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const fetchBuyerBusinessUnitByCompanyId = createAsyncThunk(
  'shipment/fetchBuyerBusinessUnitByCompanyId',
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/business-units/company', { params: companyId });
      // console.log("response.data",response.data.data)
      return response.data.data;
    } catch (error) {
      // console.error("Failed to fetch:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const fetchSellerBusinessUnit = createAsyncThunk(
  'shipment/fetchSellerBusinessUnit',
  async (rejectWithValue) => {
    try {
      const response = await apiService.get('/business-units/company');
      // console.log("inside fetchSellerBusinessUnit",response.data)
      return response.data.data;
    } catch (error) {
      // console.error("Failed to fetch:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk to Delete a product in a shipment
export const deleteShipmentProduct = createAsyncThunk(
  'shipment/deleteShipmentProduct',
  async ({ shipmentId, productId }, { rejectWithValue }) => {
    try {
      const response = await apiService.delete(`/shipments/${shipmentId}/shipment-product/${productId}`);
      return response
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Get the list of Orders to link in the shipment
export const fetchOrdersToLink = createAsyncThunk(
  'shipment/fetchOrdersToLink',
  async (queryParams, { rejectWithValue }) => {
    try {
      // const response = await apiService.get(`/orders/supplier-companies?${companyKeyId}`);
      const response = await apiService.get('/shipments/buyerOrders',
        { params: queryParams },
      );
      return response.data; // Adjust if your API response structure is different
    } catch (error) {
      // console.error("Failed to fetch suppliers:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for adding a product to a shipment
export const addShipmentProduct = createAsyncThunk(
  'shipment/addShipmentProduct',
  async ({ shipmentId, productDetails }, { rejectWithValue }) => {
    try {
      // console.log('!!Add Product!!!',productDetails,"shipmentId",shipmentId)
      const url = `/shipments/${shipmentId}/shipment-product`;
      const response = await apiService.post(url, productDetails); // Adjust the endpoint as necessary
      return response.data; //   the API responds with the added product data
    } catch (error) {
      return rejectWithValue(error.response.data); // Handling API errors
    }
  }
);

// Async thunk to Edit a product in a shipment
export const updateShipmentProduct = createAsyncThunk(
  'shipment/updateShipmentProduct',
  async ({ shipmentId, shipmentProductId, productData }, { rejectWithValue }) => {
    // console.log('!!update Product!!!',shipmentId,shipmentProductId, productData)
    try {
      // console.log("inside try");
      const response = await apiService.patch(`/shipments/${shipmentId}/products/${shipmentProductId}`, productData);
      return response.data; // Assuming the API responds with the updated product data
    } catch (error) {
      // console.log("inside catch",error);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

export const fetchProductsByShipmentId = createAsyncThunk(
  'shipment/fetchProductsByShipmentId',
  async (queryParams, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/shipments/${queryParams.shipmentId}`, { params: queryParams });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Decline/Reject Shipment
export const declineShipment = createAsyncThunk(
  'shipment/declineShipment',
  async ({ shipmentId, reason, action }, { rejectWithValue }) => {
    try {
      // console.log("shipmentId, reason, action",shipmentId, reason, action)
      const response = await apiService.patch(`/shipments`, {
        reason, action, shipmentId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Cancel Shipment
export const cancelShipment = createAsyncThunk(
  'shipment/cancelShipment',
  async ({ shipmentId, reason, action }, { rejectWithValue }) => {
    try {
      // console.log("shipmentId, reason, action",shipmentId, reason, action)
      const response = await apiService.patch(`/shipments`, {
        reason, action, shipmentId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Delete Shipment 
export const deleteShipmentById = createAsyncThunk(
  'shipment/deleteShipmentById',
  async (shipmentId, { rejectWithValue }) => {
    try {
      //  console.log('!!DELETE SHIPMENT!!!',shipmentId)      
      const response = await apiService.delete(`/shipments/${shipmentId}`);
      return response
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for adding a product to a shipment
export const addShipmentProductLots = createAsyncThunk(
  'shipment/addShipmentProductLots',
  async ({ shipmentProductId, stockLotId, consumeProductQty }, { rejectWithValue }) => {
    // console.log("sadsjdhjwd");
    try {
      // console.log('!!Add addShipmentProductLots!!!',stockLotId)
      const url = `/shipments/shipmentProductLots`;
      const response = await apiService.post(url, { shipmentProductId, stockLotId, consumeProductQty }); // Adjust the endpoint as necessary
      return response.data; //   the API responds with the added product data
    } catch (error) {
      return rejectWithValue(error.response.data); // Handling API errors
    }
  }
);

// To update the received qty
export const updateShipmentProductQtyReceived = createAsyncThunk(
  'shipment/updateShipmentProductQtyReceived',
  async ({ shipmentId, shipmentProductId, productQtReceived }, { rejectWithValue }) => {
    // console.log('!!update updateShipmentProductQtyReceived!!!',shipmentProductId, productQtReceived)
    try {
      // console.log("inside try");
      const response = await apiService.patch(`/shipments/productQuantity/${shipmentId}`, { shipmentProductId, productQtReceived });
      return response.data; // Assuming the API responds with the updated product data
    } catch (error) {
      // console.log("inside catch",error);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// To receive a single shipment
export const receiveShipmentById = createAsyncThunk(
  'shipment/receiveShipmentById',
  async ({ shipmentId, receive }, { rejectWithValue }) => {
    // console.log('!!Receiev receiveShipmentById!!!',receive)
    try {
      // console.log("inside try");
      const response = await apiService.patch(`/shipments/${shipmentId}`, { receive });
      return response.data; // Assuming the API responds with the updated product data
    } catch (error) {
      // console.log("inside catch",error);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// To fetch the stocks for the shipment
export const fetchStocks = createAsyncThunk(
  'shipment/fetchStocks',
  async (queryParams, thunkAPI) => {
    try {
      const response = await apiService.get('/stocks',
        { params: queryParams },
      );
      // console.log("response.data in fetchStocks",response.data)
      return response.data;
    } catch (error) {
      // console.log("error : ", error)
      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// To fetch the stock by id
export const fetchStockById = createAsyncThunk(
  'shipment/fetchStockById',
  async (stockLotId, thunkAPI) => {
    try {
      const response = await apiService.get(`/stocks/${stockLotId}`
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// To fetch the stock added in particular product in a shipment
export const fetchStocksByShipmentProductId = createAsyncThunk(
  'shipment/fetchStocksByShipmentProductId',
  async (shipmentProductId, thunkAPI) => {
    try {
      const response = await apiService.get(`/shipments/${shipmentProductId}/shipment-lots`
      );
      // console.log("response.data in fetchStocksByShipmentProductId",response.data)
      return response.data;
    } catch (error) {
      // console.log("error : ", error)
      return thunkAPI.rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk to Delete a stock lot added in the product in a shipment
export const deleteShipmentProductLot = createAsyncThunk(
  'shipment/deleteShipmentProductLot',
  async ({ shipmentId, shipmentProductLotId }, { rejectWithValue }) => {
    try {
      const response = await apiService.delete(`/shipments/${shipmentId}/product-lots/${shipmentProductLotId}`);
      return response
    } catch (error) {
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk to update the lot quantity in product in a shipment
export const updateShipmentProductLotQty = createAsyncThunk(
  'shipment/updateShipmentProductLotQty',
  async ({ shipmentProductLotId, consumeProductQty }, { rejectWithValue }) => {
    // console.log('!!update updateShipmentProductLotQty!!!',shipmentProductLotId,consumeProductQty)
    try {
      // console.log("inside try");
      const response = await apiService.patch(`/shipments/productLot`, { shipmentProductLotId, consumeProductQty });
      return response.data; // Assuming the API responds with the updated product data
    } catch (error) {
      // console.log("inside catch",error);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Async thunk for fetching an shipment by ID
export const fetchLotsByShipmentId = createAsyncThunk(
  'shipment/fetchLotsByShipmentId',
  async ({ shipmentId, type }, { rejectWithValue }) => {
    try {
      const response = await apiService.get(`/shipments/${shipmentId}`, { params: { type } });
      // console.log("response.data",response.data);
      return response.data; // response
    } catch (error) {
      // console.error("Failed to fetch shipment details:", error.message);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// Place Shipment
export const placeShipment = createAsyncThunk(
  'shipment/placeShipment',
  async ({ shipmentId, action }, { rejectWithValue }) => {
    try {
      // console.log("shipmentId, action",shipmentId, action)
      const response = await apiService.patch(`/shipments`, {
        action, shipmentId
      });
      toast.success('Shipment Placed successfully');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "An unexpected error occurred";
      toast.error(errorMessage);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// To link Buyer PO in a shipment
export const linkUnlinkBuyerPurchaseOrder = createAsyncThunk(
  'shipment/linkUnlinkBuyerPurchaseOrder',
  async ({ shipmentId, orderId, action }, { rejectWithValue }) => {
    try {
      // console.log("shipmentId, action",shipmentId, action)
      const response = await apiService.patch(`/shipments/${shipmentId}`, {
        action, orderId
      });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.msg || "An unexpected error occurred";
      toast.error(errorMessage);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// to auto-generate the order if PO is not linked
export const generateShipmentAutoOrder = createAsyncThunk(
  'shipment/generateShipmentAutoOrder',
  async (shipmentData, { rejectWithValue }) => {
    try {
      const response = await apiService.post('/orders', shipmentData);
      return response.data;
    } catch (error) {
      // console.log("error.message",error.message)
      const errorMessage = error.response?.data?.msg || "An unexpected error occurred";
      toast.error(errorMessage);
      return rejectWithValue(error.response.data || error.message);
    }
  }
);

// The shipment slice
export const shipmentSlice = createSlice({
  name: 'shipment',
  initialState,
  reducers: {
    resetShipmentState: (state) => {
      state.shipmentData = [];
      state.shipmentDetails = {};
      state.buyerBusinessUnit = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
    .addCase(fetchUOM.pending, (state) => {
      state.loading = true;
    })
    .addCase(fetchUOM.fulfilled, (state, action) => {
      state.loading = false;
      state.uom = action.payload;
    })
    .addCase(fetchUOM.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
      // Handling fetchShipments
      .addCase(fetchShipments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.loading = false;
        state.shipmentData = action.payload.data;
        state.totalRecords = action.payload.totalRecords;
      })
      .addCase(fetchShipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload; // Ensure this is in a usable format
      })
      // Handling createShipment
      .addCase(createShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShipment.fulfilled, (state, action) => {
        state.loading = false;
        state.shipmentDetails = action.payload;
      })
      .addCase(createShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handling fetchShipmentById
      .addCase(fetchShipmentById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchShipmentById.fulfilled, (state, action) => {
        state.loading = false;
        state.shipmentDetails = action.payload;
        // console.log("action.payload fetchShipmentById",action.payload)
      })
      .addCase(fetchShipmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(editShipment.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(editShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handling fetchBuyersByCompanyId
      .addCase(fetchBuyersByCompanyId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBuyersByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        state.buyers = action.payload; // Assuming the payload is the buyers list
      })
      .addCase(fetchBuyersByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handling fetchBuyerBusinessUnitByCompanyId
      .addCase(fetchBuyerBusinessUnitByCompanyId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBuyerBusinessUnitByCompanyId.fulfilled, (state, action) => {
        state.loading = false;
        state.buyerBusinessUnit = action.payload;
        // console.log("action.payload fetchBuyerBusinessUnitByCompanyId",state.data)
        state.error = null;
      })
      .addCase(fetchBuyerBusinessUnitByCompanyId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handling fetchSellerBusinessUnit
      .addCase(fetchSellerBusinessUnit.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSellerBusinessUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.sellerBusinessUnit = action.payload;
        // console.log("action.payload fetchSellerBusinessUnit",state.data)
        state.error = null;
      })
      .addCase(fetchSellerBusinessUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Shipment Product
      .addCase(deleteShipmentProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShipmentProduct.fulfilled, (state, action) => {
        state.loading = false;
        // const orderIndex = state.data.findIndex(shipment => shipment.id === action.payload.orderId);
        // if (orderIndex !== -1) {
        //   const products = state.data[orderIndex].products || [];
        //   state.data[orderIndex].products = products.filter(product => product.id !== action.payload.productId);
        // }
      })
      .addCase(deleteShipmentProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrdersToLink.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrdersToLink.fulfilled, (state, action) => {
        state.loading = false;
        state.linkingOrders = action.payload; //   the payload is the suppliers list
      })
      .addCase(fetchOrdersToLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //  Handling Add Product in Shipment
      .addCase(addShipmentProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addShipmentProduct.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("action.payload addShipmentProduct",action.payload)
        state.productData = action.payload;
        state.error = null;
      })
      .addCase(addShipmentProduct.rejected, (state, action) => {
        state.loading = false;
        // console.log("addShipmentProduct action.payload",action.payload)
        state.error = action.payload;
      })
      // Update Shipment Product
      .addCase(updateShipmentProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShipmentProduct.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("updateShipmentProduct",action.payload)
        state.productDetails = action.payload;
      })
      .addCase(updateShipmentProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProducts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.productList = action.payload;
      })
      .addCase(fetchProducts.rejected, (state) => {
        state.status = 'failed';
      })
      // Decline/Reject Shipment
      .addCase(declineShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(declineShipment.fulfilled, (state, action) => {
        state.loading = false;
        // Update the state based on the action outcome
        // For example, marking the specific shipment as 'Rejected'
        // console.log("state.shipmentData in reject api",state.shipmentData)
        const index = state.shipmentData.findIndex(shipment => shipment.shipmentId === action.meta.arg.shipmentId);
        if (index !== -1) {
          state.shipmentData[index] = { ...state.shipmentData[index], shipmentStatus: 'Rejected', reason: action.meta.arg.reason };
        }
      })
      .addCase(declineShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Cancel Shipment
      .addCase(cancelShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelShipment.fulfilled, (state, action) => {
        state.loading = false;
        // Update the state based on the action outcome
        // For example, marking the specific shipment as 'Cancelled'
        // console.log("state.shipmentData in reject api",state.shipmentData)
        const index = state.shipmentData.findIndex(shipment => shipment.shipmentId === action.meta.arg.shipmentId);
        if (index !== -1) {
          state.shipmentData[index] = { ...state.shipmentData[index], shipmentStatus: 'Cancelled', reason: action.meta.arg.reason };
        }
      })
      .addCase(cancelShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Shipment 
      .addCase(deleteShipmentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShipmentById.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteShipmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //  Handling Add Product Lot in Shipment
      .addCase(addShipmentProductLots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addShipmentProductLots.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("action.payload addShipmentProductLots",action.payload)
        state.error = null;
      })
      .addCase(addShipmentProductLots.rejected, (state, action) => {
        state.loading = false;
        // console.log("addShipmentProductLots state.error",action.payload)
        state.error = action.payload;
      })
      .addCase(updateShipmentProductQtyReceived.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShipmentProductQtyReceived.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("updateShipmentProductQtyReceived",action.payload)
        state.productDetails = action.payload;
      })
      .addCase(updateShipmentProductQtyReceived.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(receiveShipmentById.pending, (state) => {
        state.loading = true;
      })
      .addCase(receiveShipmentById.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("receiveShipmentById",action.payload)
        //state.productDetails = action.payload;
      })
      .addCase(receiveShipmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchStocks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStocks.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("stockList",action.payload.data)
        state.stockList = action.payload.data;
        state.stockTotalRecords = action.payload.totalRecords // TODO: TO BE FECHED DIFFERENTLY ONCE API IS UPDATED
      })
      .addCase(fetchStocks.rejected, (state, action) => {
        state.loading = false;
        // console.log("action.payload in fetchStocks",action.payload)
        state.error = action.payload; // Ensure this is in a usable format
      })
      .addCase(fetchStockById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStockById.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("fetchStockById fulfilled",action.payload.data)
        state.stockDetails = action.payload.data;
      })
      .addCase(fetchStockById.rejected, (state, action) => {
        state.loading = false;
        // console.log("action.payload in fetchStockById",action.payload)
        state.error = action.payload; // Ensure this is in a usable format
      })
      .addCase(fetchStocksByShipmentProductId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchStocksByShipmentProductId.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("stockData",action.payload.data)
        state.stockData = action.payload.data;
      })
      .addCase(fetchStocksByShipmentProductId.rejected, (state, action) => {
        state.loading = false;
        // console.log("action.payload in fetchStocksByShipmentProductId",action.payload)
        state.error = action.payload; // Ensure this is in a usable format
      })
      // Delete Shipment Product Lot
      .addCase(deleteShipmentProductLot.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShipmentProductLot.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteShipmentProductLot.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateShipmentProductLotQty.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShipmentProductLotQty.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("updateShipmentProduct",action.payload)
      })
      .addCase(updateShipmentProductLotQty.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchLotsByShipmentId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLotsByShipmentId.fulfilled, (state, action) => {
        state.loading = false;
        state.stocks = action.payload;
      })
      .addCase(fetchLotsByShipmentId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Place Shipment
      .addCase(placeShipment.pending, (state) => {
        state.loading = true;
      })
      .addCase(placeShipment.fulfilled, (state, action) => {
        state.loading = false;
        // console.log("placeShipment fulfilled",state.shipmentData)
        // const index = state.shipmentData.findIndex(shipment => shipment.shipmentId === action.meta.arg.shipmentId);
        // if (index !== -1) {
        //   state.shipmentData[index] = { ...state.shipmentData[index], shipmentStatus: 'Placed' };
        // }
      })
      .addCase(placeShipment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(linkUnlinkBuyerPurchaseOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(linkUnlinkBuyerPurchaseOrder.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(linkUnlinkBuyerPurchaseOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateShipmentAutoOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(generateShipmentAutoOrder.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(generateShipmentAutoOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
  },
});

// Export actions and reducer
export const { resetShipmentState } = shipmentSlice.actions;
export default shipmentSlice.reducer;
